/**
 * Effect estimation utilities for action previews
 * These provide player-facing estimates, not actual resolution logic
 */

export interface CampaignEffectEstimate {
  recognitionBoost: number
  votersInfluenced: number
  efficiency: 'excellent' | 'good' | 'moderate' | 'low'
  efficiencyReason: string
}

/**
 * Estimate the effects of a campaign action for preview purposes
 * Actual resolution may vary based on events, competition, etc.
 */
export function estimateCampaignEffect(
  intensity: number,
  currentRecognition: number,
  isNational: boolean,
  districtPopulation?: number
): CampaignEffectEstimate {
  // Base recognition boost scales with intensity
  const baseBoost = intensity * 2

  // Diminishing returns at high recognition (harder to gain when already well-known)
  const diminishingFactor = Math.max(0.2, 1 - currentRecognition / 150)
  const recognitionBoost = Math.round(baseBoost * diminishingFactor)

  // National campaigns are less efficient per district but have wider reach
  const efficiencyMultiplier = isNational ? 0.5 : 1

  // Estimate voters influenced based on intensity and population
  const baseVoters = districtPopulation ? districtPopulation * 0.02 : 1200
  const votersInfluenced = Math.round(
    intensity * baseVoters * efficiencyMultiplier
  )

  // Determine efficiency rating
  let efficiency: CampaignEffectEstimate['efficiency']
  let efficiencyReason: string

  if (currentRecognition >= 80) {
    efficiency = 'low'
    efficiencyReason = 'Already very well-known'
  } else if (currentRecognition >= 60) {
    efficiency = 'moderate'
    efficiencyReason = 'Diminishing returns at high recognition'
  } else if (currentRecognition >= 30) {
    efficiency = 'good'
    efficiencyReason = 'Good potential for growth'
  } else {
    efficiency = 'excellent'
    efficiencyReason = 'Low recognition means high growth potential'
  }

  // Adjust for national campaigns
  if (isNational && efficiency !== 'low') {
    efficiencyReason += ' (national reach but less focused)'
  }

  return {
    recognitionBoost,
    votersInfluenced,
    efficiency,
    efficiencyReason,
  }
}

/**
 * Get display color for efficiency rating
 */
export function getEfficiencyColor(
  efficiency: CampaignEffectEstimate['efficiency']
): string {
  switch (efficiency) {
    case 'excellent':
      return 'text-green-600'
    case 'good':
      return 'text-blue-600'
    case 'moderate':
      return 'text-amber-600'
    case 'low':
      return 'text-red-600'
  }
}

/**
 * Format efficiency for display
 */
export function formatEfficiency(
  efficiency: CampaignEffectEstimate['efficiency']
): string {
  return efficiency.charAt(0).toUpperCase() + efficiency.slice(1)
}

// ============================================================================
// FUNDRAISE ACTION EFFECTS
// ============================================================================

import { DONOR_BLOCS, type DonorBlocKey } from '@/constants/donors'
import type { IdeologyVector } from '@/constants/ideology'

export interface FundraiseYieldEstimate {
  min: number
  max: number
  expected: number
}

/**
 * Calculate Euclidean distance between two ideology vectors
 */
function calculateIdeologyDistance(
  a: IdeologyVector,
  b: IdeologyVector
): number {
  let sumSquares = 0
  for (let i = 0; i < 7; i++) {
    sumSquares += Math.pow(a[i] - b[i], 2)
  }
  return Math.sqrt(sumSquares)
}

/**
 * Calculate how well a party's ideology aligns with a donor bloc.
 * Returns a percentage from 0-100.
 *
 * For grassroots donors, alignment is based on party momentum instead of ideology.
 */
export function calculateDonorAlignment(
  partyIdeology: IdeologyVector,
  donorBloc: DonorBlocKey,
  partyMomentum?: number
): number {
  const bloc = DONOR_BLOCS[donorBloc]

  if (donorBloc === 'grassroots') {
    // Grassroots cares about momentum, not ideology
    // Base 50% alignment + up to 50% from momentum (0-100 scale)
    const momentum = partyMomentum ?? 0
    return Math.min(100, Math.max(0, 50 + momentum * 0.5))
  }

  // Calculate Euclidean distance between party ideology and donor's ideal
  const distance = calculateIdeologyDistance(partyIdeology, bloc.idealIdeology)

  // Max possible distance is sqrt(7 * 200^2) ≈ 529
  // We use 280 as effective max (most realistic distance range)
  // Convert distance to alignment: 0 distance = 100%, 280 distance = 0%
  const alignment = Math.max(0, 100 - (distance / 2.8))
  return Math.round(alignment)
}

/**
 * Estimate the money raised from a fundraise action.
 * Actual resolution may vary based on events, competition, etc.
 */
export function estimateFundraiseYield(
  donorBloc: DonorBlocKey,
  alignment: number,
  intensity: number
): FundraiseYieldEstimate {
  const bloc = DONOR_BLOCS[donorBloc]

  // Alignment multiplier: 50% at 0 alignment, 100% at 100 alignment
  const alignmentMultiplier = 0.5 + (alignment / 100) * 0.5

  const baseYield = bloc.baseYieldPerAP * intensity * alignmentMultiplier
  const variance = bloc.yieldVariance * intensity

  return {
    min: Math.max(0, Math.round(baseYield - variance)),
    max: Math.round(baseYield + variance),
    expected: Math.round(baseYield),
  }
}

/**
 * Get alignment quality rating
 */
export function getAlignmentRating(
  alignment: number
): 'excellent' | 'good' | 'moderate' | 'poor' {
  if (alignment >= 75) return 'excellent'
  if (alignment >= 50) return 'good'
  if (alignment >= 25) return 'moderate'
  return 'poor'
}

/**
 * Get display color for alignment rating
 */
export function getAlignmentColor(
  rating: 'excellent' | 'good' | 'moderate' | 'poor'
): string {
  switch (rating) {
    case 'excellent':
      return 'text-green-600'
    case 'good':
      return 'text-blue-600'
    case 'moderate':
      return 'text-amber-600'
    case 'poor':
      return 'text-red-600'
  }
}

/**
 * Get background color for alignment bar
 */
export function getAlignmentBarColor(alignment: number): string {
  if (alignment >= 75) return 'bg-green-500'
  if (alignment >= 50) return 'bg-blue-500'
  if (alignment >= 25) return 'bg-amber-500'
  return 'bg-red-500'
}

// ============================================================================
// ORGANIZE ACTION EFFECTS
// ============================================================================

import {
  getBaseGain,
  getDiminishingFactor,
  getFocusBonus,
  getOrganizationBenefit,
  getEfficiencyRating,
  ORGANIZE_EFFECTS,
  type OrganizingFocus,
} from '@/constants/organize'

export interface OrganizeEffectEstimate {
  baseGain: number
  focusBonus: number
  diminishingFactor: number
  totalGain: number
  projectedOrganization: number
  efficiency: 'excellent' | 'good' | 'moderate' | 'low'
  efficiencyReason: string
  benefitDescription: string
  showDiminishingWarning: boolean
}

/**
 * Estimate the effects of an organize action for preview purposes.
 * Actual resolution may vary based on events, competition, etc.
 */
export function estimateOrganizeEffect(
  intensity: number,
  currentOrganization: number,
  focus: OrganizingFocus | '' = ''
): OrganizeEffectEstimate {
  const baseGain = getBaseGain(intensity)
  const focusBonus = getFocusBonus(currentOrganization, focus)
  const diminishingFactor = getDiminishingFactor(
    currentOrganization,
    focus as OrganizingFocus
  )

  const rawGain = baseGain + focusBonus
  const totalGain = Math.round(rawGain * diminishingFactor)
  const projectedOrganization = Math.min(
    ORGANIZE_EFFECTS.ORG_MAX,
    currentOrganization + totalGain
  )

  const { rating: efficiency, reason: efficiencyReason } =
    getEfficiencyRating(currentOrganization)
  const benefitDescription = getOrganizationBenefit(projectedOrganization)
  const showDiminishingWarning =
    currentOrganization > ORGANIZE_EFFECTS.HIGH_ORG_WARNING_THRESHOLD

  return {
    baseGain,
    focusBonus,
    diminishingFactor,
    totalGain,
    projectedOrganization,
    efficiency,
    efficiencyReason,
    benefitDescription,
    showDiminishingWarning,
  }
}
